import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { DiscrepancyForm, useRolodexEdit } from 'features/fde'
import { useKestrelViewerContext } from 'features/kestrelViewer/types/KestrelViewerInterfaces'
import { MarkupPopupContainer } from 'features/kestrelViewer/ui/popupMenu/shared/MarkupPopupContainer'
import { useRolodexEntries } from 'features/rolodex'
import debounce from 'lodash/debounce'
import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import {
  Form781AData,
  Form781AStatus,
  FormEntry,
  RolodexEntryType,
} from 'types'

const NavigationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
`

const NavigationButton = styled.button<{ disabled?: boolean }>`
  background: rgba(59, 130, 246, 0.2);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: ${(props) =>
    props.disabled ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.9)'};
  padding: 0.5rem;
  border-radius: 4px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:hover:not(:disabled) {
    background: rgba(59, 130, 246, 0.3);
    border-color: rgba(59, 130, 246, 0.5);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const CounterText = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.85rem;
  font-weight: 500;
  text-align: center;
`

interface Form781AMenuProps {
  kestrelModelId: string
  workOrders: FormEntry[]
}

export const Form781AMenu: FC<Form781AMenuProps> = ({
  kestrelModelId,
  workOrders,
}) => {
  const { setSelectedMarkup, popupMenuStartPosition } =
    useKestrelViewerContext()
  const [currentIndex, setCurrentIndex] = useState(0)
  const { rolodexPatch } = useRolodexEdit()

  const form781AId = useMemo(() => `${kestrelModelId}-781a`, [kestrelModelId])

  const { entries: form781AEntries } = useRolodexEntries({
    type: RolodexEntryType.FORM_781A,
    ids: [form781AId],
  })

  const form781AEntry = form781AEntries[0]

  // Get full form data from Rolodex, falling back to wrapper around workOrders
  const [localFormData, setLocalFormData] = useState<Form781AData | null>(null)

  const formData = useMemo<Form781AData>(() => {
    if (localFormData) return localFormData
    if (form781AEntry?.data?.formData) {
      return form781AEntry.data.formData as Form781AData
    }
    return {
      name: 'Untitled Form',
      status: Form781AStatus.InProgress,
      createdAt: new Date().toISOString(),
      header: { from: '', to: '', mds: '', serialNumber: '' },
      entries: workOrders,
    }
  }, [localFormData, form781AEntry, workOrders])

  // Get the subset of entries that match the workOrders (by ID) for navigation
  const workOrderIds = useMemo(
    () => new Set(workOrders.map((wo) => wo.id)),
    [workOrders]
  )

  const visibleEntryIndices = useMemo(
    () =>
      formData.entries
        .map((entry, idx) => ({ entry, idx }))
        .filter(({ entry }) => workOrderIds.has(entry.id))
        .map(({ idx }) => idx),
    [formData.entries, workOrderIds]
  )

  const currentEntryIndex = visibleEntryIndices[currentIndex] ?? 0

  const debouncedSave = useRef(
    debounce(
      async (
        formId: string,
        form: Form781AData,
        patch: typeof rolodexPatch
      ) => {
        const existingExcluded = form781AEntry?.data?.excludedAnnotations || []
        await patch([
          {
            id: formId,
            data: {
              excludedAnnotations: existingExcluded,
              formData: form,
            },
          },
        ])
      },
      500
    )
  ).current

  const handleFormChange = useCallback(
    (newFormData: Form781AData) => {
      setLocalFormData(newFormData)
      debouncedSave(form781AId, newFormData, rolodexPatch)
    },
    [form781AId, debouncedSave, rolodexPatch]
  )

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleNext = () => {
    if (currentIndex < visibleEntryIndices.length - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  return (
    <MarkupPopupContainer
      popupMenuStartPosition={popupMenuStartPosition!}
      setSelectedMarkup={setSelectedMarkup}
      width='56rem'>
      <NavigationHeader>
        <NavigationButton
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          title='Previous work order'>
          <ChevronLeftIcon />
        </NavigationButton>
        <CounterText>
          Work Order {currentIndex + 1} of {visibleEntryIndices.length}
        </CounterText>
        <NavigationButton
          onClick={handleNext}
          disabled={currentIndex === visibleEntryIndices.length - 1}
          title='Next work order'>
          <ChevronRightIcon />
        </NavigationButton>
      </NavigationHeader>

      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{ cursor: 'default' }}>
        <DiscrepancyForm
          entryIndex={currentEntryIndex}
          formData={formData}
          onFormChange={handleFormChange}
        />
      </div>
    </MarkupPopupContainer>
  )
}
